import React, { useEffect, useState, useCallback } from "react";
import LabelInput from "../../../../../shared/components/labelInput";
import { useForm } from "react-hook-form";
import VehicleLookup from "../../../../../shared/components/VehicleLookup/VehicleLookup";
import {
  fetchLicenceAuth,
  fetchKeeper,
  fetchOwner,
  fetchOverNight,
} from "../../../../../shared/services/simpleListItemsService";
import useAppStateStore from "../../../../../shared/store/AppStateStore";
import NewDateTimePicker from "../../../../../shared/components/newDateTimePicker";
import RadioGroup from "../../../../../shared/components/RadioGroup";
import SpVehicleMapper from "../mappers/spVehicleMapper";
import QuoteFormWrapper from "../../../../../shared/components/quoteFormWrapper";
import SupportNavigationButtons from "../../../components/support/SupportNavigationButtons";
import { handleSupportNext } from "../../../utils/supportNavUtils";
import { useNavigate } from "react-router-dom";
import NewDatePicker from "../../../../../shared/components/newDatePicker";
import ukRegistrationValidation from "../../../../../shared/utils/ukRegistrationValidation";
import AddressLookup from "../../../../../shared/components/addressLookup";
import FieldStack from "../../../../../shared/components/FieldStack";
import QuoteStepHeading from "../../../../../shared/components/QuoteStepHeading";

const SpVehicle = () => {
  const formMethods = useForm({
    defaultValues: SpVehicleMapper.get(),
    mode: "onChange",
  });
  const {
    register,
    formState: { errors, isValid },
    watch,
    setValue,
    control,
    trigger,
    handleSubmit,
  } = formMethods;

  const [lic, setlic] = useState([]);
  const [keeper, setKeeper] = useState([]);
  const [owner, setOwner] = useState([]);
  const [overNight, setOverNight] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingOwnerKeeper, setIsLoadingOwnerKeeper] = useState(true);
  let STATE = useAppStateStore();
  const navigate = useNavigate();

  const vehicleHireOptions = [
    {
      value: "private_hire",
      label: "Private Hire",
    },
    { value: "public_hire", label: "Public Hire/Hackney carriage" },
  ];
  const transmissionType = [
    {
      value: "manual",
      label: "Manual",
    },
    { value: "automatic", label: "Automatic" },
  ];

  useEffect(() => {
    setIsLoading(true);
    fetchLicenceAuth()
      .then((uber) => setlic(uber))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    setIsLoading(true);
    fetchOverNight()
      .then((overNightData) => setOverNight(overNightData))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    setIsLoadingOwnerKeeper(true);
    Promise.all([fetchKeeper(), fetchOwner()])
      .then(([keeperData, ownerData]) => {
        setKeeper(keeperData);
        setOwner(ownerData);
      })
      .finally(() => setIsLoadingOwnerKeeper(false));
  }, []);

  useEffect(() => {
    if (watch("prevManualVehicleReg")) {
      setValue("prevLookupVehicleReg", null);
    }
    if (watch("prevLookupVehicleReg")) {
      setValue("prevManualVehicleReg", null);
    }
  }, [watch("prevLookupVehicleReg"), watch("prevManualVehicleReg")]);

  const convertArrayFormat = useCallback((array) => {
    if (!Array.isArray(array)) {
      return [];
    }
    return array.map((item) => ({
      value: item.listItemId,
      label: item.listItemDescription,
    }));
  }, []);

  const onSubmit = (data) => {
    if (
      watch("overNightHome") === "No" &&
      ((STATE.enterAddressGarage ?? STATE.enterAddress) ||
        data.lookUpAddress === null ||
        data.lookUpAddress === undefined)
    ) {
      return;
    }

    SpVehicleMapper.set(data);
    handleSupportNext(navigate);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <QuoteFormWrapper>
        <QuoteStepHeading title="Change vehicle" className="tw-mb-6" />
        <FieldStack className="tw-w-full">
        <VehicleLookup
          STATE={STATE}
          prevVeh={true}
          formMethods={formMethods}
          showLookupQuestions={false}
          registrationValidation={ukRegistrationValidation}
        />
        {watch("prevLookupData") && (
          <>
            <hr className="tw-m-0 tw-w-full tw-border-0 tw-border-b tw-border-solid tw-border-dis-divider" />
            <RadioGroup
              name="exitingLicenseAuth"
              mainLabel="Will your vehicle continue to be licenced by the existing licencing authority disclosed on your policy?"
              register={register}
              trigger={trigger}
              errors={errors.exitingLicenseAuth}
            />
            {watch("exitingLicenseAuth") === "No" && (
              <LabelInput
                id="LicenseAuth"
                label="What will be the licencing authority?"
                type="select"
                options={convertArrayFormat(lic)}
                control={control}
                placeholder="Select licence authority"
                register={register("LicenseAuth", { required: true })}
                errors={errors.LicenseAuth}
                watch={watch("LicenseAuth")}
                trigger={trigger}
                isSearchable={true}
                isLoading={isLoading}
              />
            )}
            <LabelInput
              id="hireType"
              label="Will your vehicle be plated for Private hire or Public Hire/Hackney carriage?"
              type="select"
              options={vehicleHireOptions}
              control={control}
              errors={errors.hireType}
              placeholder="Select hire type"
              register={register("hireType", { required: "Required" })}
              watch={watch("hireType")}
              trigger={trigger}
            />
            <LabelInput
              id="transmissionType"
              label="Is your vehicle a Manual or Automatic?"
              type="select"
              options={transmissionType}
              control={control}
              errors={errors.transmissionType}
              placeholder="Select transmission type"
              register={register("transmissionType", { required: "Required" })}
              watch={watch("transmissionType")}
              trigger={trigger}
            />
            <NewDatePicker
              name="purchaseDate"
              label="When did you purchase the vehicle? Or when will you purchase the vehicle?"
              errors={errors.purchaseDate}
              setValue={setValue}
              trigger={trigger}
              control={control}
              rules={{ required: "Date is required" }}
              watch={watch}
            />
            <LabelInput
              id="marketValue"
              label="What is the value of the vehicle?"
              type="number"
              placeholder="e.g 7,000"
              register={register("marketValue", {
                required: "Required",
                maxLength: { value: 6, message: "Price must be a number with at most 6 digits" },
                pattern: { value: /^[0-9]{1,6}$/, message: "Price must be a number with at most 6 digits" },
              })}
              errors={errors.marketValue}
              watch={watch("marketValue")}
              trigger={trigger}
              maxLength={6}
              onInput={(e) => {
                e.target.value = e.target.value.slice(0, 6);
              }}
            />
            <LabelInput
              id="locOverNight"
              label="Where is your vehicle located overnight?"
              type="select"
              options={convertArrayFormat(overNight)}
              control={control}
              placeholder="Select location"
              register={register("locOverNight", { required: true })}
              errors={errors.locOverNight}
              watch={watch("locOverNight")}
              trigger={trigger}
              isSearchable={true}
              isLoading={isLoading}
            />
            <RadioGroup
              name="overNightHome"
              mainLabel="Is your vehicle kept at your home address overnight?"
              register={register}
              trigger={trigger}
              errors={errors.overNightHome}
              onNo={() => {
                setValue("lookUpAddress", null);
                setValue("address", "");
                setValue("postcode", "");
              }}
            />
            {watch("overNightHome") === "No" && (
              <AddressLookup
                register={register}
                watch={watch}
                control={control}
                setValue={setValue}
                trigger={trigger}
                errors={errors}
                STATE={STATE}
                scope="garage"
                labelHouse="House name or number"
                labelPostCode="Overnight postcode"
              />
            )}
            <LabelInput
              id="keeper"
              label="Who is the registered keeper of the vehicle?"
              type="select"
              control={control}
              options={convertArrayFormat(keeper)}
              placeholder="Select keeper"
              register={register("keeper", { required: "Required" })}
              errors={errors.keeper}
              watch={watch("keeper")}
              trigger={trigger}
              isLoading={isLoadingOwnerKeeper}
            />
            <LabelInput
              id="owner"
              label="Who is the registered owner of the vehicle?"
              type="select"
              control={control}
              options={convertArrayFormat(owner)}
              placeholder="Select owner"
              register={register("owner", { required: "Required" })}
              errors={errors.owner}
              watch={watch("owner")}
              trigger={trigger}
              isLoading={isLoadingOwnerKeeper}
            />
            <NewDateTimePicker
              name="vehicleEffectiveDateTime"
              label="Effective Date and Time"
              errors={errors.vehicleEffectiveDateTime}
              setValue={setValue}
              control={control}
              rules={{ required: "Date is required" }}
              watch={watch}
              defaultValue={watch("vehicleEffectiveDateTime")}
              minDate={new Date()}
              trigger={trigger}
            />
          </>
        )}
        </FieldStack>
      </QuoteFormWrapper>
      <SupportNavigationButtons isValid={isValid} />
    </form>
  );
};

export default SpVehicle;
